// ── YEAR IDENTITY — a module asks for the year it actually MEANS ─────────────
//
// Silas deals with SEVERAL kinds of "year" at once, and they are different
// numbers on the same day. On 2026-09-10 the book's crop year is 2026, the
// wheat being drilled right now is crop year 2027, the wheat RATE TABLES a
// quote reads are the 2027 actuarial vintage, PRF is settling 2026 intervals
// while its own forward quote is 2027, and December corn is the 2026 contract.
// Every one of those is correct, and collapsing them into one CURRENT_YEAR is
// how a page comes to show two years at once without saying so.
//
// ⚠ THIS FILE DELIBERATELY DOES NOT EXPORT A UNIVERSAL "CURRENT YEAR".
// There is no such thing here. What it exports is:
//   - the IDENTITIES, so a parameter can say which kind it is;
//   - the RELATIONS between crop years (prior / forward), which are arithmetic;
//   - the SEASON rule, which is an RMA fact and was previously written inline;
//   - `availableCropYears`, which reads the DATA and never invents a year;
//   - `defaultCropYearFor`, the ONE place a module's default-year policy lives.
//
// `CURRENT_CROP_YEAR` in App.jsx stays exactly as it is — it is already
// data-derived ("the crop year the book carries the most premium for") and it
// is the correct INPUT to the rules here. This module does not replace it; it
// stops every surface from doing its own arithmetic on top of it.

// ── The identities ───────────────────────────────────────────────────────────
// A string tag, not an enum class. It rides alongside a number so a function
// signature can say what it wants and `assertYearIdentity` can check it.
export const YEAR_IDENTITY = {
  // The wall clock. `new Date().getFullYear()`. Weather, calendars, sync logs.
  CALENDAR: "calendar",
  // RMA's insurance crop year. What `policies.crop_year` / `crop_coverage`
  // carry. A fall crop's crop year is the year it is HARVESTED, not drilled.
  CROP: "crop",
  // The actuarial / rate-table vintage a quote is priced from. Usually the
  // crop year being quoted, but it is a SEPARATE fact: RMA can publish a
  // vintage late, and a quote must then say which vintage it used.
  ACTUARIAL: "actuarial",
  // PRF / rainfall index year. Settles ~14 months, so its last two intervals
  // publish in the NEXT calendar year — see the PRF release-watch section.
  RAINFALL: "rainfall",
  // Futures delivery / marketing year. December 2026 corn is a 2026 contract
  // even when it is quoted in 2027.
  CONTRACT: "contract",
  // FSA program year (ARC/PLC). `arc_plc_reference.program_year`.
  PROGRAM: "program",
  // FSA-578 tax year. `farmer_578s.tax_year`.
  TAX: "tax",
};

const IDENTITY_VALUES = new Set(Object.values(YEAR_IDENTITY));

// ── Plausibility ─────────────────────────────────────────────────────────────
// A crop year in this app's world. The floor is deliberately NOT 1948 (the PRF
// index history floor) — that is a RAINFALL history bound, not a crop year a
// policy is written in, and letting it through here is how a FIPS code or a
// coverage level sneaks in where a year belongs.
//
// ⚠ `Number(null)`, `Number("")` and `Number(false)` are all 0, which is
// finite — so `Number.isFinite` alone lets a null through and it gets silently
// answered as some year. This is a RANGE test for exactly that reason; the
// same trap is recorded against `scoEcoBands` in CLAUDE.md.
export const CROP_YEAR_MIN = 2000;
export const CROP_YEAR_MAX = 2100;

export function isPlausibleCropYear(y) {
  if (y == null || y === "" || typeof y === "boolean") return false;
  const n = Number(y);
  return Number.isInteger(n) && n >= CROP_YEAR_MIN && n <= CROP_YEAR_MAX;
}

// Coerce to a crop-year number, or null. NEVER a default — a caller that
// cannot establish a year must say UNKNOWN, not pick one.
export function asCropYear(y) {
  return isPlausibleCropYear(y) ? Number(y) : null;
}

// ── assertYearIdentity ───────────────────────────────────────────────────────
// Throws on a value that cannot be the identity it claims. This is a
// programming-error guard, not input validation: it fires when a caller passes
// a coverage level, a FIPS code, a null or ANOTHER identity's year into a slot
// that means something specific. It throws rather than warns for the reason
// `assertNumeralsContained` throws — a warning reaches the page anyway.
export function assertYearIdentity(value, identity, where) {
  if (!IDENTITY_VALUES.has(identity)) {
    throw new Error(`assertYearIdentity: unknown identity ${JSON.stringify(identity)} at ${where || "?"}`);
  }
  if (!isPlausibleCropYear(value)) {
    throw new Error(
      `${where || "assertYearIdentity"}: expected a ${identity} year, got ${JSON.stringify(value)}`
    );
  }
  return Number(value);
}

// ── Relations. Arithmetic, but named, so a `- 1` at a call site is a fact. ───
export function priorCropYear(cy) {
  const n = asCropYear(cy);
  return n == null ? null : n - 1;
}
export function forwardCropYear(cy) {
  const n = asCropYear(cy);
  return n == null ? null : n + 1;
}

// ── The season rule ──────────────────────────────────────────────────────────
// ⚠ A FALL CROP IS PLANTED THE AUTUMN BEFORE ITS CROP YEAR. Winter wheat
// drilled in September 2026 is insured as CROP YEAR 2027 and harvested in July
// 2027. So at any moment the two lenses review DIFFERENT crop years, and
// running both off one year compares this season's spring acres against last
// season's wheat and calls the difference a swing.
//
// This was written inline in CropReviewView; it is here so the sign-off, the
// added-land compute and the print path cannot each re-derive it differently.
export const SEASONS = ["spring", "fall"];
export const SEASON_LABEL = { spring: "Spring Crops", fall: "Fall / Wheat" };

export function isSeason(s) {
  return SEASONS.includes(s);
}

// The crop year a season's ACTIVE CAMPAIGN is in, given the book's own crop
// year. This is the DEFAULT the page opens on — never a lock.
export function seasonCropYear(season, bookCropYear) {
  const b = asCropYear(bookCropYear);
  if (b == null) return null;
  if (!isSeason(season)) {
    throw new Error(`seasonCropYear: unknown season ${JSON.stringify(season)}`);
  }
  return season === "fall" ? b + 1 : b;
}

// ── availableCropYears ───────────────────────────────────────────────────────
// Derived from the DATA, never from arithmetic on today's date. The brief's
// rule: "Do not create fake zero-data years." A year that carries no row for
// the thing a page reads is not offered, because selecting it renders an empty
// page that reads as a broken screen rather than as a year with no data.
//
// `rows` is any array of records carrying a year; `field` names the column.
// Pass `require` to demand the year ALSO appear in a second set (e.g. offer a
// crop year only where coverage actually exists, not merely a policy shell).
export function availableCropYears(rows, field = "crop_year", opts = {}) {
  const seen = new Set();
  for (const r of rows || []) {
    const y = asCropYear(r && r[field]);
    if (y != null) seen.add(y);
  }
  let years = [...seen];
  if (opts.require instanceof Set) years = years.filter((y) => opts.require.has(y));
  else if (Array.isArray(opts.require)) {
    const req = new Set(opts.require.map(asCropYear).filter((y) => y != null));
    years = years.filter((y) => req.has(y));
  }
  return years.sort((a, b) => b - a); // newest first — the picker's natural order
}

// ── defaultCropYearFor ───────────────────────────────────────────────────────
// THE ONE PLACE A MODULE'S DEFAULT-YEAR POLICY LIVES.
//
// ⚠ THERE IS DELIBERATELY NO GLOBAL DEFAULT. Crop Review's spring lens, its
// fall lens, the wheat renewal advisor, PRF and grain marketing legitimately
// open on DIFFERENT years on the same morning, and a single rule that made
// them agree would be wrong on four of the five.
//
// A module appears here only when it actually calls this function. A registry
// entry for a surface that does not call it is dead code that reads as live —
// the surfaces audited but not yet wired are listed in the year inventory,
// not here.
export const MODULE_YEAR_RULES = {
  // Crop Review: season-aware. Spring reviews the crop in the ground now, fall
  // reviews the wheat being drilled this autumn (bookCropYear + 1).
  cropReview: {
    identity: YEAR_IDENTITY.CROP,
    rule: "season",
    why: "spring = the book's crop year; fall = the wheat drilled this autumn, which is next crop year",
  },
  // ARC/PLC farm election summary: the FSA PROGRAM year whose election / enrollment window is
  // open today — the one closing SOONEST where two overlap (from Nov 2, 2026 a wheat grower faces
  // the 2026 and the 2027 windows at once) — else the next to open, else the most recent. The
  // windows are FSA's announced dates, passed in by the caller; nothing here types a year.
  arcPlcElection: {
    identity: YEAR_IDENTITY.PROGRAM,
    rule: "electionWindow",
    why: "the FSA program year whose ARC/PLC election window is open (soonest to close), else the next to open",
  },
};

// The program year an election-window rule points at, from FSA's windows ({ year: { opens,
// closes } }, ISO dates) and a date. Null when no window is known — never a guessed year.
export function electionWindowYear(windows, today) {
  const t = String(today || new Date().toISOString()).slice(0, 10);
  const iso = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
  const ws = Object.entries(windows || {})
    .map(([y, w]) => ({ y: asCropYear(y), opens: w && w.opens, closes: w && w.closes }))
    .filter((w) => w.y != null && iso(w.opens) && iso(w.closes));
  if (!ws.length) return null;
  const open = ws.filter((w) => w.opens <= t && t <= w.closes).sort((a, b) => a.closes.localeCompare(b.closes));
  if (open.length) return open[0].y;
  const next = ws.filter((w) => w.opens > t).sort((a, b) => a.opens.localeCompare(b.opens));
  if (next.length) return next[0].y;
  return ws.sort((a, b) => b.closes.localeCompare(a.closes))[0].y;
}

// ── electionYearContext — THE ONE ARC/PLC PROGRAM-YEAR CONTEXT ───────────────
// Every ARC/PLC surface (the Coverage Advisor panel, its one-county election sheet, the farm
// election summary, the Full Strategy Report) reads its program year from THIS, so no surface
// decides a year of its own (Kevin, 2026-09-19).
//
// ⚠ IT NEVER FALLS BACK TO THE NEAREST YEAR THAT HAS DATA. The CURRENT year is FSA's open
// election window, whatever has been published. Once the active election is a year FSA has not
// published figures for, the context is PENDING — the surfaces say so and compute nothing. A
// nearest-available fallback would put 2026's benchmark and reference prices under a 2027
// heading, which is the one thing this context exists to prevent.
//
// `published`: the program years FSA figures are loaded for. `windows`: FSA's announced
// election windows ({ year: { opens, closes } }). `storedYear` / `urlYear`: a pick — honoured
// only when that year is OFFERED. Offered = every windowed year that is published, plus the
// current year and any other open window even when unpublished (those read PENDING). A year
// with no announced window is not offered: nothing here types a year.
//
// Returns { year, current, pending, status, offered: [{ year, status, published, window }],
//           source, why, rejected? }. `year` is the selected program year (null = unknown);
// `pending` is true when that year has no published FSA figures.
export function electionYearContext({ published, windows, today, storedYear, urlYear } = {}) {
  const t = String(today || new Date().toISOString()).slice(0, 10);
  const pub = new Set((published || []).map(asCropYear).filter((y) => y != null));
  const iso = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
  const current = electionWindowYear(windows, t);
  const offered = Object.entries(windows || {})
    .map(([y, w]) => ({ year: asCropYear(y), window: w }))
    .filter((o) => o.year != null && o.window && iso(o.window.opens) && iso(o.window.closes))
    .map((o) => {
      const open = o.window.opens <= t && t <= o.window.closes;
      const status = o.year === current ? "current" : open ? "open" : o.window.closes < t ? "previous" : "upcoming";
      return { year: o.year, window: o.window, status, published: pub.has(o.year) };
    })
    .filter((o) => o.published || o.status === "current" || o.status === "open")
    .sort((a, b) => b.year - a.year);
  const isOffered = (y) => y != null && offered.some((o) => o.year === y);
  const fromUrl = asCropYear(urlYear), stored = asCropYear(storedYear);
  let year = null, source = "unknown", why = "no FSA election window is known";
  if (isOffered(fromUrl)) { year = fromUrl; source = "url"; }
  else if (isOffered(stored)) { year = stored; source = "session"; }
  else if (current != null) { year = current; source = "default"; why = MODULE_YEAR_RULES.arcPlcElection.why; }
  const sel = offered.find((o) => o.year === year) || null;
  const pending = year != null && !pub.has(year);
  if (source !== "default" && sel) why = `picked — the ${year} program year (${sel.status})`;
  if (pending) why = `FSA has not published the ${year} program data`;
  const out = { year, current, pending, status: sel ? sel.status : null, offered, source, why };
  const rejected = (fromUrl != null && !isOffered(fromUrl)) ? fromUrl : (stored != null && !isOffered(stored)) ? stored : null;
  return rejected != null ? { ...out, rejected } : out;
}

// Returns { year, source, why } — `source` says WHERE the year came from, so a
// screen can label a default differently from a user's pick, and `null` year
// means UNKNOWN (never 0, never today's date).
export function defaultCropYearFor(module, ctx = {}) {
  const rule = MODULE_YEAR_RULES[module];
  if (!rule) {
    throw new Error(
      `defaultCropYearFor: no year policy registered for module ${JSON.stringify(module)} — ` +
      `add one to MODULE_YEAR_RULES rather than deciding the default at the call site`
    );
  }
  const available = Array.isArray(ctx.available) ? ctx.available.map(asCropYear).filter((y) => y != null) : null;

  let want = null;
  if (rule.rule === "season") {
    want = seasonCropYear(ctx.season, ctx.bookCropYear);
  } else if (rule.rule === "electionWindow") {
    want = electionWindowYear(ctx.windows, ctx.today);
  } else {
    want = asCropYear(ctx.bookCropYear);
  }
  if (want == null) return { year: null, source: "unknown", why: rule.rule === "electionWindow" ? "no FSA election window is known" : "no book crop year established" };

  // If we know what the data actually holds, the default must be a year that
  // is really there. Falling back to the NEAREST available year keeps the page
  // rendering something true rather than an empty screen — and it reports that
  // it did, so "there is no 2027 book yet" never reads as "2027 is empty".
  if (available && available.length && !available.includes(want)) {
    let best = available[0];
    for (const y of available) if (Math.abs(y - want) < Math.abs(best - want)) best = y;
    return {
      year: best,
      source: "nearest-available",
      why: `${rule.why}; CY${want} has no data loaded, showing the nearest year that does`,
    };
  }
  return { year: want, source: "default", why: rule.why };
}

// ── Session/URL persistence, PER LENS ────────────────────────────────────────
// Kevin: "Remember the user's last selected year independently per lens."
// A single remembered year would carry a fall pick onto the spring lens, where
// it is a year out — which is the mixed-year failure this whole pass is about.
//
// A year is not farmer-scoped and carries no customer identity, so it is safe
// in the URL. Storage failures are swallowed: a private window that refuses
// sessionStorage must still render the page.
const YEAR_STORE_PREFIX = "silas_year_v1";

export function yearStoreKey(module, season) {
  return `${YEAR_STORE_PREFIX}.${module}.${season || "-"}`;
}

export function readStoredYear(module, season, storage) {
  try {
    const s = storage || (typeof sessionStorage !== "undefined" ? sessionStorage : null);
    if (!s) return null;
    return asCropYear(s.getItem(yearStoreKey(module, season)));
  } catch {
    return null;
  }
}

export function writeStoredYear(module, season, year, storage) {
  try {
    const s = storage || (typeof sessionStorage !== "undefined" ? sessionStorage : null);
    if (!s) return;
    const y = asCropYear(year);
    if (y == null) s.removeItem(yearStoreKey(module, season));
    else s.setItem(yearStoreKey(module, season), String(y));
  } catch {
    /* a browser that refuses storage still gets a working page */
  }
}

// ── resolveSelectedYear ──────────────────────────────────────────────────────
// The full precedence, in ONE place so the picker, the data reads and the
// print path cannot disagree about which year the page is on:
//
//   1. an explicit URL parameter   (a deep link somebody sent)
//   2. this session's last pick FOR THIS LENS
//   3. the module's season-aware default
//
// A stored or linked year that the data cannot support is REFUSED and reported
// rather than honoured — otherwise a stale deep link renders an empty page
// that reads as a broken screen.
export function resolveSelectedYear(module, ctx = {}) {
  const available = Array.isArray(ctx.available)
    ? ctx.available.map(asCropYear).filter((y) => y != null)
    : null;
  const supported = (y) => y != null && (!available || !available.length || available.includes(y));

  const fromUrl = asCropYear(ctx.urlYear);
  if (fromUrl != null && supported(fromUrl)) return { year: fromUrl, source: "url" };

  const stored = asCropYear(ctx.storedYear);
  if (stored != null && supported(stored)) return { year: stored, source: "session" };

  const d = defaultCropYearFor(module, ctx);
  const rejected =
    (fromUrl != null && !supported(fromUrl)) ? fromUrl :
    (stored != null && !supported(stored)) ? stored : null;
  return rejected != null ? { ...d, rejected } : d;
}
